import type { UsdLedgerRowLike } from '@kioskgaming/ui';
import type { CounterpartResolved } from './UsdWalletActivityView';
import type { LedgerMetadata } from './usdWalletLedgerUtils';
import { isUsdOutflowLedgerType, parseLedgerMetadata } from './usdWalletLedgerUtils';

type UsdLedgerParty = NonNullable<UsdLedgerRowLike['fromParty']>;

function ledgerNum(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function ledgerStr(v: unknown): string {
  return v != null ? String(v).trim() : '';
}

function ownerAdminPath(ownerType: string, ownerId: string): string | undefined {
  const t = ownerType.trim().toLowerCase();
  const id = ownerId.trim();
  if (!id) return undefined;
  if (t === 'agent') return `/agents/${encodeURIComponent(id)}`;
  if (t === 'super_agent') return `/super-agents/${encodeURIComponent(id)}`;
  if (t === 'shop') return `/shops/${encodeURIComponent(id)}`;
  if (t === 'player') return `/users/${encodeURIComponent(id)}`;
  return undefined;
}

function partyPrimaryLabel(party: UsdLedgerParty | null | undefined): string {
  if (!party) return '';
  const raw = party.label?.trim();
  if (raw) {
    return raw.replace(/^(Agent|Shop|Player|Super Agent|Platform):\s+/i, '$1 · ');
  }
  const role = party.roleLabel?.trim() ?? '';
  const name = party.displayName?.trim() || party.ownerId?.trim() || '';
  if (role && name) return `${role} · ${name}`;
  return name || role;
}

function resolveOwnerParty(row: UsdLedgerRowLike, type: string): UsdLedgerParty | null | undefined {
  const t = type.trim().toLowerCase();
  if (isUsdOutflowLedgerType(t)) return row.fromParty ?? undefined;
  if (t === 'deposit') return row.toParty ?? undefined;
  return row.fromParty ?? row.toParty ?? undefined;
}

function resolveCounterpartyParty(row: UsdLedgerRowLike, type: string): UsdLedgerParty | null | undefined {
  const t = type.trim().toLowerCase();
  if (isUsdOutflowLedgerType(t)) return row.toParty ?? undefined;
  if (t === 'deposit') return row.fromParty ?? undefined;
  return row.toParty ?? row.fromParty ?? undefined;
}

function counterpartDisplayName(row: UsdLedgerRowLike, type: string): string {
  const t = type.trim().toLowerCase();
  const cp = resolveCounterpartyParty(row, type);
  if (isUsdOutflowLedgerType(t) && cp?.ownerType === 'external') {
    return 'External destination';
  }
  const label = partyPrimaryLabel(cp);
  if (label) return label;
  return isUsdOutflowLedgerType(t) ? 'External destination' : 'Payment processor';
}

function extractWithdrawalInfo(meta: LedgerMetadata, netDelta: number) {
  const payout =
    meta.payout && typeof meta.payout === 'object' ? (meta.payout as LedgerMetadata) : {};
  return {
    walletAddress: ledgerStr(
      payout.destinationAddress ?? payout.walletAddress ?? meta.destinationAddress ?? meta.walletAddress,
    ),
    coin: ledgerStr(payout.coin ?? meta.coin ?? meta.payoutCoin),
    network: ledgerStr(payout.network ?? meta.payoutNetwork ?? meta.network),
    note: ledgerStr(payout.note ?? meta.note ?? meta.withdrawalNote),
    requestedNetUsd: ledgerNum(meta.requestedNetUsd ?? meta.netPayoutUsd ?? Math.abs(netDelta)),
  };
}

function extractDepositInfo(meta: LedgerMetadata) {
  return {
    depositor: ledgerStr(meta.depositorName ?? meta.depositor),
    fromAddress: ledgerStr(meta.fromAddress ?? meta.sourceAddress),
    coin: ledgerStr(meta.coin),
    network: ledgerStr(meta.network ?? meta.payoutNetwork),
    note: ledgerStr(meta.note),
  };
}

export type MappedUsdWalletActivity = {
  type: string;
  status: string;
  createdAt: unknown;
  amountNet: number;
  amountGross: number;
  netDelta: number;
  balanceNetBefore: number;
  balanceNetAfter: number;
  balanceGrossBefore: number;
  balanceGrossAfter: number;
  provider: string;
  transactionId: string;
  usdWalletId: string;
  ownerId: string;
  ledgerRow: UsdLedgerRowLike;
  referenceType: string;
  referenceId: string;
  referencePrimaryLabel: string;
  ownerType: string;
  ownerPrimaryLabel: string;
  ownerResolved: { label: string; to?: string } | null;
  counterpartDisplayName: string;
  counterpartResolved: CounterpartResolved | null;
  creditsAmount: number;
  metadata: LedgerMetadata;
  withdrawalInfo?: ReturnType<typeof extractWithdrawalInfo>;
  depositInfo?: ReturnType<typeof extractDepositInfo>;
};

export function mapUsdLedgerRowToActivity(row: UsdLedgerRowLike): MappedUsdWalletActivity {
  const meta = parseLedgerMetadata(row);
  const type = ledgerStr(row.type);
  const status = ledgerStr(row.status) || 'completed';
  const referenceType = ledgerStr(row.referenceType ?? row.reference_type);
  const referenceId = ledgerStr(row.referenceId ?? row.reference_id);
  const provider = ledgerStr(row.provider ?? meta.provider);
  const transactionId = ledgerStr(row.transactionId ?? row.transaction_id);
  const balanceNetBefore = ledgerNum(row.balanceNetBefore ?? row.balance_net_before);
  const balanceNetAfter = ledgerNum(row.balanceNetAfter ?? row.balance_net_after);
  const balanceGrossBefore = ledgerNum(row.balanceGrossBefore ?? row.balance_gross_before);
  const balanceGrossAfter = ledgerNum(row.balanceGrossAfter ?? row.balance_gross_after);
  const amountNet = ledgerNum(row.amountNet ?? row.amount_net);
  const amountGross = ledgerNum(row.amountGross ?? row.amount_gross);
  const netDelta = balanceNetAfter - balanceNetBefore;

  const rowRec = row as Record<string, unknown>;
  const wallet = (rowRec.usdWallet ?? rowRec.usd_wallet) as
    | Record<string, unknown>
    | undefined;
  const walletOwnerType = ledgerStr(wallet?.ownerType ?? wallet?.owner_type);
  const walletOwnerId = ledgerStr(wallet?.ownerId ?? wallet?.owner_id);
  const usdWalletId = ledgerStr(wallet?.id ?? wallet?.usdWalletId);

  const ownerParty = resolveOwnerParty(row, type);
  const ownerType = ledgerStr(ownerParty?.ownerType) || walletOwnerType;
  const ownerId = ledgerStr(ownerParty?.ownerId) || walletOwnerId;
  const ownerPrimaryLabel = partyPrimaryLabel(ownerParty) || walletOwnerFallback(row);

  const cpParty = resolveCounterpartyParty(row, type);
  const cpLabel = partyPrimaryLabel(cpParty);
  const counterpartResolved: CounterpartResolved | null = cpParty
    ? {
        label: cpLabel || counterpartDisplayName(row, type),
        shortName:
          cpParty.displayName?.trim() ||
          cpLabel.split(' · ').pop()?.trim() ||
          counterpartDisplayName(row, type),
        to:
          cpParty.ownerType && cpParty.ownerType !== 'external' && cpParty.ownerId
            ? ownerAdminPath(cpParty.ownerType, cpParty.ownerId)
            : undefined,
      }
    : null;

  const creditsAmount = ledgerNum(meta.credits_out ?? meta.creditsOut);

  const referencePrimaryLabel = referenceType
    ? referenceType
        .split('_')
        .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : ''))
        .join(' ')
    : 'Reference';

  const ownerPath = ownerAdminPath(ownerType, ownerId);

  return {
    type,
    status,
    createdAt: row.createdAt ?? row.created_at,
    amountNet,
    amountGross,
    netDelta,
    balanceNetBefore,
    balanceNetAfter,
    balanceGrossBefore,
    balanceGrossAfter,
    provider,
    transactionId,
    usdWalletId,
    ownerId,
    ledgerRow: row,
    referenceType,
    referenceId,
    referencePrimaryLabel,
    ownerType,
    ownerPrimaryLabel,
    ownerResolved: ownerPrimaryLabel
      ? { label: ownerPrimaryLabel, to: ownerPath }
      : null,
    counterpartDisplayName: counterpartDisplayName(row, type),
    counterpartResolved,
    creditsAmount,
    metadata: meta,
    ...(type.toLowerCase() === 'withdrawal'
      ? { withdrawalInfo: extractWithdrawalInfo(meta, netDelta) }
      : {}),
    ...(type.toLowerCase() === 'deposit' ? { depositInfo: extractDepositInfo(meta) } : {}),
  };
}

function walletOwnerFallback(row: UsdLedgerRowLike): string {
  const rowRec = row as Record<string, unknown>;
  const wallet = (rowRec.usdWallet ?? rowRec.usd_wallet) as
    | Record<string, unknown>
    | undefined;
  const ownerType = ledgerStr(wallet?.ownerType ?? wallet?.owner_type).toLowerCase();
  if (ownerType === 'agent') return 'Agent wallet';
  if (ownerType === 'super_agent') return 'Super agent wallet';
  if (ownerType === 'shop') return 'Shop wallet';
  if (ownerType === 'player') return 'Player wallet';
  if (ownerType === 'platform') return 'Platform wallet';
  return 'USD wallet';
}
